/**
 * Major-step definitions and progress computation for the Audit Process
 * page's top workflow stepper (Review & Audit Scheduling -> Audit ->
 * Technical Review -> Certificate Issuance -> Audit Completed).
 *
 * Extracted out of index.jsx so this can be unit-tested without mounting the
 * (8000+ line) page component, matching the other *Utils.js files already
 * colocated in this directory.
 */

// The auditor-facing sub-steps (Audit Plan / Audit / Audit Report /
// Corrective Action) collapse into a single "Audit" card. Corrective Action
// in particular is conditional — a clean audit with zero findings never gets
// a CORRECTIVE_ACTION task at all, so requiring it (as the old 4-sub-step
// model did) permanently stuck the stepper on "Audit" even once later stages
// were done. completionCodes: ['AUDIT'] makes the single core Audit task the
// only signal this major needs.
export const AUDIT_WORKFLOW_MAJOR_STEPS = [
  {
    id: 'initial-certification',
    label: 'Review & Audit Scheduling',
    subSteps: [
      {
        id: 'application-review',
        label: 'Application Review',
        codes: ['APPLICATION_REVIEW', 'ASSIGN_NEXT_PROCESS_APPLICATION_REVIEWER'],
      },
      {
        id: 'audit-scheduling',
        label: 'Audit Scheduling',
        codes: ['AUDIT_SCHEDULING', 'ASSIGN_NEXT_PROCESS_SALES', 'ASSIGN_NEXT_PROCESS_SCHEDULER'],
      },
    ],
    completionCodes: [],
  },
  {
    id: 'audit',
    label: 'Audit',
    subSteps: [],
    completionCodes: ['AUDIT'],
  },
  {
    id: 'technical-review',
    label: 'Technical Review',
    subSteps: [],
    completionCodes: ['TECHNICAL_REVIEW', 'ASSIGN_NEXT_PROCESS_TECHNICAL', 'ASSIGN_NEXT_PROCESS_TECHNICAL_REVIEWER'],
  },
  {
    id: 'certificate-issuance',
    label: 'Certificate Issuance',
    subSteps: [
      {
        id: 'certificate-decision',
        label: 'Certificate Decision',
        codes: ['CERTIFICATE_DECISION', 'CERTIFICATE_ISSUANCE', 'ASSIGN_NEXT_PROCESS_CERTIFICATION_AUTHORITY'],
      },
      {
        id: 'issue-certificate',
        label: 'Issue Certificate',
        codes: ['ISSUE_CERTIFICATE', 'ASSIGN_NEXT_PROCESS_CERTIFICATE_ISSUER'],
      },
    ],
    completionCodes: ['CERTIFICATE_ISSUANCE'],
  },
  {
    id: 'audit-completed',
    label: 'Audit Completed',
    subSteps: [],
    completionCodes: [],
    terminal: true,
  },
];

const normalizeTaskCode = (value) => String(value || '').trim().toUpperCase();

const isTaskDone = (status) => {
  const normalized = String(status || '').trim().toUpperCase();
  return normalized === 'DONE' || normalized === 'COMPLETED' || normalized === 'CLOSED';
};

export const buildAuditWorkflowProgress = (
  auditTasks,
  {
    schedulingConfirmed = false,
    isInitialStage1 = false,
    includeCertificateIssuance = true,
    completedSubStepIds: externallyCompletedSubStepIds = [],
  } = {}
) => {
  const tasks = Array.isArray(auditTasks) ? auditTasks : [];
  const externalCompletedSubSteps = new Set((externallyCompletedSubStepIds || []).map((id) => String(id)));

  const allMatchingTasksDone = (codes) => {
    const normalizedCodes = new Set((codes || []).map(normalizeTaskCode));
    const matching = tasks.filter((task) => normalizedCodes.has(normalizeTaskCode(task?.taskTypeCode)));
    return matching.length > 0 && matching.every((task) => isTaskDone(task?.status));
  };

  const isSubStepCompleted = (subStep) => (
    externalCompletedSubSteps.has(subStep.id)
  ) || (
    subStep.id === 'audit-scheduling' && schedulingConfirmed
  ) || allMatchingTasksDone(subStep.codes);

  const majorSteps = (isInitialStage1 || !includeCertificateIssuance)
    ? AUDIT_WORKFLOW_MAJOR_STEPS.filter((step) => step.id !== 'certificate-issuance')
    : AUDIT_WORKFLOW_MAJOR_STEPS;

  const ownSignalStates = majorSteps.map((major) => {
    const completedByCompletionCode = allMatchingTasksDone(major.completionCodes || []);

    let completed = false;
    if (major.terminal) {
      completed = false;
    } else if (major.subSteps.length > 0) {
      completed = major.subSteps.every((subStep) => isSubStepCompleted(subStep)) || completedByCompletionCode;
    } else {
      completed = completedByCompletionCode;
    }

    return { ...major, completed };
  });

  // Progress is strictly scope-local.  In particular, never infer completion
  // from a later task merely existing: Integrated Audit scopes can share task
  // identifiers across Stage 1, Stage 2, and later cycles.  Treating a later
  // task as evidence made an unfinished Stage 2 and future Surveillance /
  // Recertification cycles appear complete.
  // Preserve the workflow order as well: an inconsistent/stale downstream
  // task must not render as complete ahead of an unfinished prerequisite.
  const majorStates = ownSignalStates.map((major, index) => ({
    ...major,
    completed: major.completed && ownSignalStates.slice(0, index).every((earlier) => earlier.completed),
  }));

  const currentMajor = majorStates.find((major) => !major.terminal && !major.completed)
    || majorStates.find((major) => major.terminal)
    || majorStates[majorStates.length - 1];

  let currentSubStepId = null;
  let currentLabel = currentMajor?.label || '-';

  if (currentMajor && !currentMajor.terminal && currentMajor.subSteps.length > 0) {
    const nextSubStep = currentMajor.subSteps.find((subStep) => !isSubStepCompleted(subStep)) || null;
    if (nextSubStep) {
      currentSubStepId = nextSubStep.id;
      currentLabel = nextSubStep.label;
    } else {
      currentLabel = currentMajor.label;
    }
  }

  if (currentMajor?.terminal) {
    currentLabel = currentMajor.label;
  }

  return {
    currentMajorId: currentMajor?.id || null,
    currentSubStepId,
    currentLabel,
    isInitialStage1,
    completedMajorIds: majorStates.filter((major) => major.completed).map((major) => major.id),
    completedSubStepIds: majorStates.flatMap((major) => major.subSteps)
      .filter((subStep) => isSubStepCompleted(subStep))
      .map((subStep) => subStep.id),
    majorSteps,
  };
};

/**
 * True once every non-terminal major step is done (currentMajor has advanced
 * to the terminal "Audit Completed" step). At that point the full multi-chip
 * breakdown is pure repetition of what the group-level stage overview above
 * it already shows — callers use this to swap in a compact one-line summary
 * instead of the full stepper + celebration banner.
 */
export const isWorkflowFullyCompleted = (workflowProgress) => {
  const majorSteps = workflowProgress?.majorSteps || AUDIT_WORKFLOW_MAJOR_STEPS;
  const terminal = majorSteps.find((major) => major.terminal);
  if (!terminal) return false;
  return workflowProgress?.currentMajorId === terminal.id;
};

/**
 * The stage-gate endpoint is authoritative for an Initial Certification
 * Stage 2 closure: it also accounts for an issued/locked certificate. Some
 * older certificate tasks have no projectServiceId, so they cannot be
 * reliably associated with a scope by the task-only progress builder.
 */
export const markWorkflowCompleted = (workflowProgress) => {
  const majorSteps = workflowProgress?.majorSteps || AUDIT_WORKFLOW_MAJOR_STEPS;
  const terminal = majorSteps.find((major) => major.terminal);
  if (!terminal) return workflowProgress;

  return {
    ...workflowProgress,
    majorSteps,
    currentMajorId: terminal.id,
    currentSubStepId: null,
    currentLabel: terminal.label,
    completedMajorIds: majorSteps.filter((major) => !major.terminal).map((major) => major.id),
    completedSubStepIds: majorSteps.flatMap((major) => major.subSteps || []).map((subStep) => subStep.id),
  };
};

/**
 * A recurring cycle cannot finish before its own audit evidence exists. This
 * is used when legacy task rows cannot identify which non-staged cycle they
 * belong to and would otherwise make a future Surveillance/Recertification
 * cycle inherit Initial Certification's completed workflow.
 */
export const markWorkflowPendingAudit = (workflowProgress) => {
  const majorSteps = workflowProgress?.majorSteps || AUDIT_WORKFLOW_MAJOR_STEPS;
  const audit = majorSteps.find((major) => major.id === 'audit');
  if (!audit) return workflowProgress;

  return {
    ...workflowProgress,
    majorSteps,
    currentMajorId: audit.id,
    currentSubStepId: null,
    currentLabel: audit.label,
    completedMajorIds: majorSteps
      .filter((major) => major.id === 'initial-certification')
      .map((major) => major.id),
    completedSubStepIds: (workflowProgress?.completedSubStepIds || [])
      .filter((id) => ['application-review', 'audit-scheduling'].includes(id)),
  };
};
